import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation, useParams, Link as RouterLink } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Autocomplete,
  Avatar,
  Chip,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  CircularProgress,
  Alert,
  Link,
  Switch,
} from "@mui/material";
import {
  PersonSearch,
  Restaurant,
  ReceiptLong,
  Close,
  Add,
  Remove,
  LocationOn,
  Phone,
  Email,
  Schedule,
  ArrowForward,
  ChevronLeft,
  ChevronRight,
  WarningAmber,
  OpenInNew,
  Person,
} from "@mui/icons-material";
import { format, addDays, subDays, isAfter, setHours, setMinutes, startOfWeek } from "date-fns";
import { getBusinessNow, getBusinessToday } from "@/utils/businessTime";
import { fr } from "date-fns/locale";
import {
  Button,
  Card,
  LoadingSpinner,
  AllergenChip,
  ErrorMessage,
  CompactAlert,
} from "@/components/ui";
import { orderService, CreateOrderData, UpdateOrderData, type Order } from "@/services/order.service";
import { customerService, Customer } from "@/services/customer.service";
import { productService, Product } from "@/services/product.service";
import { categoryService } from "@/services/category.service";
import { paymentService } from "@/services/payment.service";
import {
  formatPaymentSettlementLabel,
  PAYMENT_SETTLEMENT_IMMEDIATE_LABEL,
  PAYMENT_SETTLEMENT_LATER_LABEL,
} from "@/utils/formatPaymentMethodLabel";
import { weeklyMenuService, type MenuSlot } from "@/services/weeklyMenu.service";
import { estimationService } from "@/services/estimation.service";
import { useToastStore } from "@/store/toastStore";
import { useAuthStore } from "@/store/authStore";
import { authService } from "@/services/auth.service";
import { CustomerTypeBadge } from "@/components/ui/CustomerTypeBadge";
import { formatCustomerName } from "@/utils/formatCustomerName";
import {
  isAddressDeliverable,
  OUT_OF_DELIVERY_ZONE_MESSAGE,
} from "@/utils/addressDeliverability";

/** Parse YYYY-MM-DD en date locale (midi) pour éviter décalage en Guyane / fuseaux négatifs. */
function parseLocalDateOnly(dateStr: string): Date {
  const s = typeof dateStr === "string" ? dateStr.slice(0, 10) : "";
  const [y, m, d] = s.split("-").map(Number);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return new Date(NaN);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

/** Retourne la partie date YYYY-MM-DD d’un slot (alignée backend, sans décalage fuseau). */
function toSlotDateKey(date: string | Date): string {
  if (typeof date === "string") return date.slice(0, 10);
  return new Date(date).toISOString().slice(0, 10);
}

function toWeekdayCodeFromDate(dateStr: string): string | null {
  const date = parseLocalDateOnly(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  const day = date.getDay();
  const map = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
  return map[day] ?? null;
}

function isProductEligibleForDeliveryDate(product: Product, dateStr: string): boolean {
  if (!product.isRecurrent) return true;
  const recurrentDays = product.recurrentDays ?? [];
  if (recurrentDays.length === 0) return true;
  const weekday = toWeekdayCodeFromDate(dateStr);
  if (!weekday) return true;
  return recurrentDays.includes(weekday as typeof recurrentDays[number]);
}

// Schéma de validation
const orderSchema = z.object({
  customerId: z.string().min(1, "Le client est obligatoire"),
  customerAddressId: z.string().optional(),
  deliveryDate: z.string().min(1, "La date de livraison est obligatoire"),
  deliveryTimeSlot: z.string().optional(),
  serviceType: z.enum(["CATERING", "MEAL_DELIVERY"]),
  recurrence: z.enum(["ONCE", "DAILY", "WEEKLY", "MONTHLY"]).default("ONCE"),
  paymentMethodId: z.string().optional(),
  paymentStatusId: z.string().optional(),
  specialInstructions: z.string().optional(),
  discountAmount: z.union([z.string(), z.number()]).optional().transform((v) => (v === "" || v === undefined ? undefined : Number(String(v).replace(",", ".")))),
});

type OrderFormData = z.infer<typeof orderSchema>;

/**
 * Calcule la réduction (€) à partir de la règle entreprise correspondant à l'indice client.
 * Même logique que le backend : plafonnée par le reste à charge minimum (minChargeAmount).
 */
function getDiscountFromEnterpriseRule(
  customer: {
    indice?: number | null;
    enterprise?: {
      discountRules?: Array<{
        indexMin: number;
        indexMax: number;
        discountType: string;
        discountValue: number;
        minChargeAmount: number;
      }>;
    } | null;
  },
  orderSubtotal: number
): number {
  if (orderSubtotal <= 0) return 0;
  if (!customer.enterprise?.discountRules?.length || customer.indice == null) return 0;
  const indice = Number(customer.indice);
  const rule = customer.enterprise.discountRules.find(
    (r) => indice >= r.indexMin && indice <= r.indexMax
  );
  if (!rule) return 0;
  const minCharge = Number(rule.minChargeAmount);
  const maxDiscount = Math.max(0, orderSubtotal - minCharge);
  if (maxDiscount <= 0) return 0;
  if (rule.discountType === "FIXED") {
    return Math.min(Number(rule.discountValue), maxDiscount);
  }
  return Math.min((orderSubtotal * Number(rule.discountValue)) / 100, maxDiscount);
}

/**
 * Frais de livraison : un seul forfait par commande (coût zone ou prix fixe règle entreprise),
 * quel que soit le nombre de plats ou d'articles.
 */
function computeDeliveryFee(
  serviceType: string,
  zoneDeliveryCost: number,
  customer: {
    enterpriseId?: string | null;
    indice?: number | null;
    enterprise?: {
      discountRules?: Array<{
        indexMin: number;
        indexMax: number;
        fixedDeliveryPrice?: number | null;
      }>;
    } | null;
  } | null,
): number {
  if (serviceType === "CATERING") return 0;
  let unitPrice = zoneDeliveryCost;
  if (
    customer?.enterpriseId &&
    customer.indice != null &&
    customer.enterprise?.discountRules?.length
  ) {
    const indice = Number(customer.indice);
    const rule = customer.enterprise.discountRules.find(
      (r) => indice >= r.indexMin && indice <= r.indexMax
    );
    if (rule?.fixedDeliveryPrice != null && Number.isFinite(Number(rule.fixedDeliveryPrice))) {
      unitPrice = Number(rule.fixedDeliveryPrice);
    }
  }
  return Math.round(unitPrice * 100) / 100;
}

interface CartItem {
  productId: string;
  product: Product;
  quantity: number;
  subtotal: number;
  unitPrice: number;
  /** Formule Switch demandée (remplacement de l'accompagnement, sans surcoût). */
  isSwitch?: boolean;
}

/** Panier multi-jours : date (YYYY-MM-DD) -> lignes pour ce jour */
type CartByDate = Record<string, CartItem[]>;

/**
 * Page de formulaire pour créer une commande
 * Basée sur la maquette new_order_creation_form_1
 * Layout 3 colonnes : Client (3), Menu (5), Summary (4)
 */
interface OrderFormLocationState {
  customerId?: string;
  deliveryDate?: string;
}

export function OrderFormPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: orderId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastStore();
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const isEditMode = !!orderId;

  useEffect(() => {
    let cancelled = false;
    authService.getMe().then((me) => { if (!cancelled) setUser(me); }).catch(() => {});
    return () => { cancelled = true; };
  }, [setUser]);

  // États
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const createCustomerSuggestedNameRef = useRef<string>("");
  const [cartByDate, setCartByDate] = useState<CartByDate>({});
  const [productSearch, setProductSearch] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [isCreatingMultiple, setIsCreatingMultiple] = useState(false); // Création de plusieurs commandes (plusieurs jours)
  /** Réduction par jour de livraison (création uniquement) */
  const [discountByDate, setDiscountByDate] = useState<Record<string, number>>({});

  // Commande en édition
  const { data: order, isLoading: orderLoading, error: orderError } = useQuery({
    queryKey: ["orders", orderId],
    queryFn: () => orderService.getById(orderId!),
    enabled: isEditMode && !!orderId,
  });

  // Récupération des données
  const { data: customers = [], isLoading: customersLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: () => customerService.getAll(), // Tous les clients
  });

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ["products"],
    queryFn: () => productService.getAll({ availability: "available" }),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: () => categoryService.getAll(), // Toutes les catégories (filtrage côté frontend si nécessaire)
  });

  const { data: paymentMethods = [] } = useQuery({
    queryKey: ["paymentMethods"],
    queryFn: () => paymentService.getAllMethods(),
    retry: false,
  });

  const { data: paymentStatuses = [] } = useQuery({
    queryKey: ["paymentStatuses"],
    queryFn: () => paymentService.getAllStatuses(),
    retry: false,
  });

  const { data: customerAddresses = [] } = useQuery({
    queryKey: ["customers", selectedCustomer?.id, "addresses"],
    queryFn: () => customerService.getAddresses(selectedCustomer!.id),
    enabled: !!selectedCustomer?.id,
  });

  // Client complet (avec allergènes) pour l'alerte à la prise de commande
  const { data: fullCustomer } = useQuery({
    queryKey: ["customers", selectedCustomer?.id],
    queryFn: () => customerService.getById(selectedCustomer!.id),
    enabled: !!selectedCustomer?.id,
  });

  // Formulaire
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<OrderFormData>({
    resolver: zodResolver(orderSchema),
    defaultValues: {
      customerId: "",
      deliveryDate: getBusinessToday(), // Aujourd'hui (heure Guyane) par défaut
      deliveryTimeSlot: "11:00 - 13:00",
      serviceType: "MEAL_DELIVERY",
      recurrence: "ONCE",
      paymentMethodId: "",
      specialInstructions: "",
      discountAmount: undefined as number | undefined,
    },
  });

  // Présélection client et/ou date de livraison quand on arrive depuis la liste ou la fiche client (une seule fois)
  const preselectAppliedRef = useRef(false);
  useEffect(() => {
    const state = location.state as OrderFormLocationState | undefined;
    if (!state || (!state.customerId && !state.deliveryDate)) return;
    if (preselectAppliedRef.current) return;
    preselectAppliedRef.current = true;
    if (state.deliveryDate) setValue("deliveryDate", state.deliveryDate);
    if (state.customerId && customers.length > 0 && !order) {
      const customer = customers.find((c) => c.id === state.customerId);
      if (customer) {
        setSelectedCustomer(customer);
        setValue("customerId", customer.id);
        setValue("discountAmount", customer.discountAmount != null ? Number(customer.discountAmount) : undefined);
      }
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [customers, location.state, location.pathname, navigate, setValue, order]);

  // En création : défaut « Plus tard » (En attente) pour le statut de paiement (une seule fois)
  const statusEnAttente = paymentStatuses.find((s) => s.name === "En attente");
  const statusPaye = paymentStatuses.find((s) => s.name === "Payé");
  const paymentStatusId = watch("paymentStatusId");
  const paymentStatusDefaultSetRef = useRef(false);
  const isDriverNewOrder = user?.role === "DRIVER" && !isEditMode;
  const isForcedOnAccount = !isEditMode && selectedCustomer?.defaultPaymentMode === "EN_COMPTE";

  useEffect(() => {
    if (isEditMode || user?.role === "DRIVER" || !statusEnAttente || paymentStatusId || paymentStatusDefaultSetRef.current) return;
    paymentStatusDefaultSetRef.current = true;
    setValue("paymentStatusId", statusEnAttente.id);
  }, [isEditMode, user?.role, statusEnAttente?.id, setValue, paymentStatusId]);

  /** Livreur : « A livrer » par défaut (règles livraison) + règlement immédiat (Payé) */
  useEffect(() => {
    if (isEditMode || user?.role !== "DRIVER") return;
    setValue("serviceType", "MEAL_DELIVERY");
    if (statusPaye?.id) {
      setValue("paymentStatusId", statusPaye.id);
      paymentStatusDefaultSetRef.current = true;
    }
  }, [isEditMode, user?.role, statusPaye?.id, setValue]);

  useEffect(() => {
    if (!isForcedOnAccount || !statusEnAttente?.id) return;
    setValue("paymentStatusId", statusEnAttente.id);
    setValue("paymentMethodId", "");
  }, [isForcedOnAccount, statusEnAttente?.id, setValue]);

  // Préremplir le formulaire en mode édition
  const orderPopulatedRef = useRef(false);
  useEffect(() => {
    if (!isEditMode || !order || customers.length === 0 || products.length === 0 || orderPopulatedRef.current) return;
    orderPopulatedRef.current = true;
    setValue("customerId", order.customerId);
    setValue("deliveryDate", order.deliveryDate.slice(0, 10));
    setValue("deliveryTimeSlot", order.deliveryTimeSlot ?? "11:00 - 13:00");
    setValue("serviceType", order.serviceType);
    setValue("recurrence", order.recurrence);
    setValue("paymentMethodId", order.paymentMethodId ?? "");
    setValue("paymentStatusId", order.paymentStatusId ?? "");
    setValue("specialInstructions", order.specialInstructions ?? "");
    setValue("customerAddressId", order.customerAddressId ?? "");
    setValue("discountAmount", order.discountAmount != null ? Number(order.discountAmount) : undefined);
    const customer = customers.find((c) => c.id === order.customerId);
    if (customer) setSelectedCustomer(customer);
    const cartItems: CartItem[] = (order.items ?? []).map((item) => {
      const product = products.find((p) => p.id === item.productId) ?? {
        id: item.productId,
        name: (item.product as { name?: string })?.name ?? "Produit",
        price: typeof item.unitPrice === "number" ? item.unitPrice : Number(item.unitPrice),
        description: (item.product as { description?: string })?.description,
        categoryId: "",
        category: undefined,
        isVegetarian: false,
        isAvailable: true,
        displayOrder: 0,
        createdAt: "",
        updatedAt: "",
      } as Product;
      const qty = item.quantity;
      const unitPrice = typeof item.unitPrice === "number" ? item.unitPrice : Number(item.unitPrice);
      return {
        productId: item.productId,
        product,
        quantity: qty,
        unitPrice,
        subtotal: typeof item.subtotal === "number" ? item.subtotal : unitPrice * qty,
        isSwitch: Boolean((item as { isSwitch?: boolean }).isSwitch),
      };
    });
    const dateStr = order.deliveryDate.slice(0, 10);
    setCartByDate({ [dateStr]: cartItems });
  }, [isEditMode, order, customers, products, setValue]);

  const deliveryDate = watch("deliveryDate");
  const deliveryDateStr = deliveryDate?.slice(0, 10) ?? "";
  const serviceType = watch("serviceType");
  const customerAddressId = watch("customerAddressId");
  const selectedCustomerAddress = useMemo(() => {
    if (!customerAddresses.length) return null;
    return customerAddressId
      ? customerAddresses.find((a) => a.id === customerAddressId) ?? null
      : customerAddresses.find((a) => a.isDefault) ?? customerAddresses[0] ?? null;
  }, [customerAddresses, customerAddressId]);
  const selectedAddressDeliverable = selectedCustomerAddress
    ? isAddressDeliverable(selectedCustomerAddress)
    : false;
  const hasDeliverableAddress = useMemo(
    () => customerAddresses.some(isAddressDeliverable),
    [customerAddresses],
  );
  const zoneDeliveryCost = selectedAddressDeliverable
    ? Number(selectedCustomerAddress?.zone?.deliveryCost) || 0
    : 0;
  // "A emporter" (CATERING) : pas de coût de livraison
  const effectiveDeliveryFee = useMemo(
    () =>
      computeDeliveryFee(
        serviceType,
        zoneDeliveryCost,
        fullCustomer ?? selectedCustomer ?? null,
      ),
    [serviceType, zoneDeliveryCost, fullCustomer, selectedCustomer],
  );

  // Pour l’édition : un seul jour ; pour la création : plusieurs jours possibles
  const cartItemsForCurrentDate = useMemo(
    () => cartByDate[deliveryDateStr] ?? [],
    [cartByDate, deliveryDateStr],
  );
  const allCartItems = useMemo(
    () => Object.values(cartByDate).flat(),
    [cartByDate],
  );
  const cartDatesSorted = useMemo(
    () => Object.keys(cartByDate).filter((d) => (cartByDate[d]?.length ?? 0) > 0).sort(),
    [cartByDate],
  );
  const numberOfDeliveryDays = cartDatesSorted.length;

  // Menu hebdo pour la date de livraison → plats du jour (semaine en date locale pour tous fuseaux)
  const weekStartDateForMenu = deliveryDate
    ? format(startOfWeek(parseLocalDateOnly(deliveryDate), { weekStartsOn: 1 }), "yyyy-MM-dd")
    : null;
  const { data: weeklyMenu } = useQuery({
    queryKey: ["weekly-menu", weekStartDateForMenu],
    queryFn: () => weeklyMenuService.getByWeekIfExists(weekStartDateForMenu!),
    enabled: !!weekStartDateForMenu,
  });

  // Dates à vérifier pour "client a déjà une commande" : jours du panier ou date de livraison sélectionnée
  const datesToCheckForExistingOrders = useMemo(() => {
    if (cartDatesSorted.length > 0) return cartDatesSorted;
    return deliveryDateStr ? [deliveryDateStr] : [];
  }, [cartDatesSorted, deliveryDateStr]);

  // Commandes déjà enregistrées pour ce client sur les dates sélectionnées (récap)
  const { data: existingOrdersResponse } = useQuery({
    queryKey: [
      "orders",
      "customer-dates",
      selectedCustomer?.id,
      datesToCheckForExistingOrders[0],
      datesToCheckForExistingOrders[datesToCheckForExistingOrders.length - 1],
    ],
    queryFn: async () => {
      const from = datesToCheckForExistingOrders[0];
      const to = datesToCheckForExistingOrders[datesToCheckForExistingOrders.length - 1];
      const result = await orderService.getAll({
        customerId: selectedCustomer!.id,
        deliveryDateFrom: from,
        deliveryDateTo: to,
      });
      return Array.isArray(result) ? result : result.orders;
    },
    enabled:
      !isEditMode &&
      !!selectedCustomer?.id &&
      datesToCheckForExistingOrders.length > 0,
  });

  const existingOrdersOnSelectedDates = useMemo(() => {
    if (!existingOrdersResponse || !Array.isArray(existingOrdersResponse)) return [];
    const set = new Set(datesToCheckForExistingOrders);
    return (existingOrdersResponse as Order[]).filter(
      (o: Order) => {
        const dateStr = typeof o.deliveryDate === "string" ? o.deliveryDate.slice(0, 10) : "";
        return dateStr && set.has(dateStr) && o.status !== "CANCELLED";
      },
    );
  }, [existingOrdersResponse, datesToCheckForExistingOrders]);

  const existingOrderDatesFormatted = useMemo(() => {
    const dates = [...new Set(existingOrdersOnSelectedDates.map((o: Order) => (typeof o.deliveryDate === "string" ? o.deliveryDate.slice(0, 10) : "")).filter(Boolean))].sort();
    return dates.map((d: string) => format(parseLocalDateOnly(d), "dd/MM/yyyy", { locale: fr })).join(", ");
  }, [existingOrdersOnSelectedDates]);

  // Estimations pour la date de livraison : produits épuisés (commandé >= estimé)
  const { data: estimationsForDate } = useQuery({
    queryKey: ["estimations", deliveryDateStr, deliveryDateStr],
    queryFn: () => estimationService.getEstimations(deliveryDateStr, deliveryDateStr),
    enabled: !isEditMode && !!deliveryDateStr,
  });
  // Quantités déjà dans le panier pour la date de livraison courante (pour respecter le plafond d'estimation)
  const cartQtyByProductForDate = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of cartItemsForCurrentDate) {
      map[item.productId] = (map[item.productId] ?? 0) + item.quantity;
    }
    return map;
  }, [cartItemsForCurrentDate]);

  const exhaustedProductIds = useMemo(() => {
    if (!estimationsForDate) return new Set<string>();
    const exhausted = new Set<string>();
    for (const est of estimationsForDate.estimatedByProduct ?? []) {
      const real = estimationsForDate.byProduct?.find((p) => p.productId === est.productId);
      const orderedFromOrders = real?.totalQuantity ?? 0;
      const orderedInCart = cartQtyByProductForDate[est.productId] ?? 0;
      const totalOrdered = orderedFromOrders + orderedInCart;
      if (totalOrdered >= est.estimated) exhausted.add(est.productId);
    }
    return exhausted;
  }, [estimationsForDate, cartQtyByProductForDate]);

  // Plats du jour : uniquement les slots dont la date = date de livraison (comparaison date calendaire, tous fuseaux)
  const platsDuJour = useMemo(() => {
    if (!weeklyMenu?.slots?.length || !deliveryDate) return [];
    const dateStr = deliveryDate.slice(0, 10);
    const slotsForDay = weeklyMenu.slots.filter((slot: MenuSlot) => {
      const slotDate = toSlotDateKey(slot.date as string | Date);
      return slotDate === dateStr && slot.productId && slot.product;
    });
    const seen = new Set<string>();
    const availableProductIds = new Set(products.map((p) => p.id));
    return slotsForDay
      .filter((slot: MenuSlot) => {
        if (!slot.product?.id || seen.has(slot.product.id)) return false;
        if (!availableProductIds.has(slot.product.id)) return false;
        seen.add(slot.product.id);
        return true;
      })
      .map((slot: MenuSlot) => products.find((p) => p.id === slot.product!.id)!);
  }, [weeklyMenu, deliveryDate, products]);

  // Option Switch : définie au niveau du menu (créneau), pas du produit
  const platsDuJourSwitchProductIds = useMemo(() => {
    if (!weeklyMenu?.slots?.length || !deliveryDate) return new Set<string>();
    const dateStr = deliveryDate.slice(0, 10);
    const set = new Set<string>();
    for (const slot of weeklyMenu.slots) {
      const slotDate = toSlotDateKey(slot.date as string | Date);
      if (slotDate === dateStr && slot.isSwitch && slot.productId) set.add(slot.productId);
    }
    return set;
  }, [weeklyMenu, deliveryDate]);

  // Vérifier deadline (10h00) — les admin / order_clerk peuvent passer malgré tout
  const isDeadlinePassed = useMemo(() => {
    if (!deliveryDate) return false;
    const today = getBusinessToday();
    if (deliveryDate !== today) return false;

    const now = getBusinessNow();
    const deadline = setHours(setMinutes(now, 0), 10); // 10h00 heure Guyane
    return isAfter(now, deadline);
  }, [deliveryDate]);

  const canBypassDeadline =
    user?.role === "ADMIN" || user?.role === "ORDER_CLERK" || user?.role === "DRIVER";
  const isBlockedByDeadline = isDeadlinePassed && deliveryDate === getBusinessToday() && !canBypassDeadline;

  // Calcul du total (tous jours confondus)
  const subtotal = useMemo(() => {
    return allCartItems.reduce((sum, item) => sum + item.subtotal, 0);
  }, [allCartItems]);

  const deliveryTotal = effectiveDeliveryFee * Math.max(1, numberOfDeliveryDays); // 1 jour minimum pour affichage
  // Sous-total par jour (produits uniquement)
  const subtotalByDate = useMemo(() => {
    const out: Record<string, number> = {};
    cartDatesSorted.forEach((date) => {
      const items = cartByDate[date] ?? [];
      out[date] = items.reduce((s, i) => s + i.subtotal, 0);
    });
    return out;
  }, [cartByDate, cartDatesSorted]);
  // Montant du jour (produits + livraison) : la réduction s'applique sur ce montant (livraison = 0 si "A emporter")
  const amountByDate = useMemo(() => {
    const out: Record<string, number> = {};
    cartDatesSorted.forEach((date) => {
      out[date] = (subtotalByDate[date] ?? 0) + effectiveDeliveryFee;
    });
    return out;
  }, [cartDatesSorted, subtotalByDate, effectiveDeliveryFee]);
  // Réduction : en création = somme des réductions par jour (calculées sur produits + livraison), plafonnées au montant du jour
  const totalDiscountNum = isEditMode
    ? (() => {
        const v = watch("discountAmount");
        if (v === undefined || v === null) return 0;
        return Math.max(0, Number(String(v).replace(",", ".")) || 0);
      })()
    : cartDatesSorted.reduce((sum, date) => {
        const dayAmount = amountByDate[date] ?? 0;
        const discount = discountByDate[date] ?? 0;
        return sum + Math.min(discount, dayAmount);
      }, 0);
  const vatRate =
    typeof user?.enterprise?.vatRate === "number" ? user.enterprise.vatRate : 0;
  const subtotalAfterDiscount = subtotal + deliveryTotal - totalDiscountNum;
  const vat = subtotalAfterDiscount * vatRate;
  const total = Math.max(0, subtotalAfterDiscount + vat);
  /** En mode édition : afficher les montants enregistrés (HT, TVA, TTC) pour cohérence avec la fiche commande et la fiche livraison */
  const displayTotal = isEditMode && order ? Number(order.totalAmount) : total;
  const displayVatAmount = isEditMode && order?.vatAmount != null ? Number(order.vatAmount) : vat;
  const displayVatRate = isEditMode && order?.vatRate != null ? Number(order.vatRate) : vatRate;

  // Filtrage des clients pour la recherche (même logique que l'Autocomplete)
  const customersMatchingSearch = useMemo(() => {
    const search = customerSearchInput.toLowerCase().trim();
    if (!search) return customers;
    const searchNoSpaces = search.replace(/\s/g, "");
    return customers.filter((opt) => {
      const fullName = formatCustomerName(opt).toLowerCase();
      const firstName = opt.firstName?.toLowerCase() ?? "";
      const lastName = opt.lastName?.toLowerCase() ?? "";
      const contact = opt.contactPerson?.toLowerCase() ?? "";
      const email = opt.email?.toLowerCase() ?? "";
      const phone = (opt.phone ?? "").replace(/\s/g, "");
      const company = opt.enterprise?.name?.toLowerCase() ?? "";
      return (
        fullName.includes(search) ||
        firstName.includes(search) ||
        lastName.includes(search) ||
        contact.includes(search) ||
        email.includes(search) ||
        phone.includes(searchNoSpaces) ||
        company.includes(search)
      );
    });
  }, [customers, customerSearchInput]);

  // IDs des allergènes du client (fiche détaillée)
  const customerAllergenIds = useMemo(
    () => fullCustomer?.allergens?.map((ca) => ca.allergen.id) ?? [],
    [fullCustomer?.allergens],
  );

  // Produits du panier (IDs uniques) pour récupérer les allergènes
  const productIdsInCart = useMemo(() => [...new Set(allCartItems.map((i) => i.productId))], [allCartItems]);

  // Récupérer les allergènes de chaque produit du panier (si client a des allergènes renseignés)
  const productAllergenQueries = useQueries({
    queries: productIdsInCart.map((productId) => ({
      queryKey: ["product-allergens", productId],
      queryFn: () => productService.getAllergens(productId),
      enabled: customerAllergenIds.length > 0 && !!productId,
    })),
  });

  // Map productId -> allergènes du produit
  const productAllergensMap = useMemo(() => {
    const map: Record<string, { id: string; name: string; code: string; icon: string | null }[]> = {};
    productIdsInCart.forEach((productId, index) => {
      const result = productAllergenQueries[index]?.data;
      if (result?.length) map[productId] = result;
    });
    return map;
  }, [productIdsInCart, productAllergenQueries]);

  // Conflits : produits du panier qui contiennent un allergène du client (un par produit)
  const allergenConflictDetails = useMemo(() => {
    if (customerAllergenIds.length === 0) return [];
    const seen = new Set<string>();
    return allCartItems
      .map((item) => {
        if (seen.has(item.productId)) return null;
        const productAllergens = productAllergensMap[item.productId] ?? [];
        const matching = productAllergens.filter((a) => customerAllergenIds.includes(a.id));
        if (matching.length === 0) return null;
        seen.add(item.productId);
        return { productName: item.product.name, matchingAllergens: matching };
      })
      .filter((x): x is { productName: string; matchingAllergens: { id: string; name: string; code: string; icon: string | null }[] } => x !== null);
  }, [allCartItems, productAllergensMap, customerAllergenIds]);

  // Produits éligibles pour la date de livraison : plats du jour + produits récurrents
  const eligibleProductsForDate = useMemo(() => {
    const dayIds = new Set(platsDuJour.map((p) => p.id));
    const recurrent = products.filter(
      (p) =>
        p.isRecurrent === true &&
        !dayIds.has(p.id) &&
        isProductEligibleForDeliveryDate(p, deliveryDateStr)
    );
    return [...platsDuJour, ...recurrent];
  }, [platsDuJour, products, deliveryDateStr]);

  // Produits du panier non disponibles pour la date (items du jour courant uniquement)
  const unavailableCartItems = useMemo(() => {
    if (!deliveryDateStr) return [];
    const eligibleIds = new Set(eligibleProductsForDate.map((p) => p.id));
    return cartItemsForCurrentDate.filter((item) => !eligibleIds.has(item.productId));
  }, [cartItemsForCurrentDate, eligibleProductsForDate, deliveryDateStr]);
  const hasUnavailableProducts = unavailableCartItems.length > 0;

  const filteredProducts = useMemo(() => {
    let filtered = eligibleProductsForDate;
    if (productSearch) {
      const search = productSearch.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(search) ||
          p.description?.toLowerCase().includes(search),
      );
    }
    if (categoryFilter) {
      filtered = filtered.filter((p) => p.categoryId === categoryFilter);
    }
    return filtered;
  }, [eligibleProductsForDate, productSearch, categoryFilter]);

  // Allergènes des produits affichés (pour les cartes produit)
  const displayedProductIds = useMemo(() => filteredProducts.map((p) => p.id), [filteredProducts]);
  const displayedAllergenQueries = useQueries({
    queries: displayedProductIds.map((productId) => ({
      queryKey: ["product-allergens-display", productId],
      queryFn: () => productService.getAllergens(productId),
      enabled: !!productId,
    })),
  });
  const displayedProductAllergensMap = useMemo(() => {
    const map: Record<string, { id: string; name: string; code: string; icon: string | null }[]> = {};
    displayedProductIds.forEach((productId, index) => {
      const data = displayedAllergenQueries[index]?.data;
      if (data?.length) map[productId] = data;
    });
    return map;
  }, [displayedProductIds, displayedAllergenQueries]);

  // Ajouter un produit au panier pour le jour sélectionné (deliveryDate)
  const handleAddProduct = (product: Product) => {
    if (exhaustedProductIds.has(product.id)) return;
    const dayItems = cartByDate[deliveryDateStr] ?? [];
    const existingItem = dayItems.find((item) => item.productId === product.id);

    if (existingItem) {
      setCartByDate({
        ...cartByDate,
        [deliveryDateStr]: dayItems.map((item) =>
          item.productId === product.id
            ? {
                ...item,
                quantity: item.quantity + 1,
                subtotal: (item.quantity + 1) * item.unitPrice,
              }
            : item,
        ),
      });
    } else {
      setCartByDate({
        ...cartByDate,
        [deliveryDateStr]: [
          ...dayItems,
          {
            productId: product.id,
            product,
            quantity: 1,
            subtotal: Number(product.price),
            unitPrice: Number(product.price),
            isSwitch: false,
          },
        ],
      });
    }
  };

  // Modifier quantité pour un jour donné
  const handleUpdateQuantity = (productId: string, date: string, delta: number) => {
    const dayItems = cartByDate[date] ?? [];
    const next = dayItems
      .map((item) => {
        if (item.productId !== productId) return item;
        const newQuantity = Math.max(0, item.quantity + delta);
        if (newQuantity === 0) return null;
        return {
          ...item,
          quantity: newQuantity,
          subtotal: newQuantity * item.unitPrice,
        };
      })
      .filter((item): item is CartItem => item !== null);
    if (next.length === 0) {
      const nextByDate = { ...cartByDate };
      delete nextByDate[date];
      setCartByDate(nextByDate);
    } else {
      setCartByDate({ ...cartByDate, [date]: next });
    }
  };

  // Activer / désactiver la formule Switch pour une ligne du panier
  const handleToggleSwitch = (productId: string, date: string) => {
    const dayItems = cartByDate[date] ?? [];
    setCartByDate({
      ...cartByDate,
      [date]: dayItems.map((item) =>
        item.productId === productId ? { ...item, isSwitch: !item.isSwitch } : item,
      ),
    });
  };

  // Supprimer du panier (non utilisé pour l'instant, on utilise handleUpdateCartQuantity avec quantity 0)
  // const handleRemoveFromCart = (productId: string) => {
  //   setCart(cart.filter((item) => item.productId !== productId));
  // };

  // Soumission
  const createOrderMutation = useMutation({
    mutationFn: (data: CreateOrderData) => orderService.create(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      success("Commande créée avec succès");
      navigate("/orders", {
        state: { deliveryDate: variables.deliveryDate },
      });
    },
    onError: (err: any) => {
      const data = err?.response?.data;
      const msg = data?.message ?? data?.error?.message;
      if (err?.response?.status === 500 && data) {
        console.error("[OrderForm] Erreur serveur (500):", data);
      }
      showError(msg || "Erreur lors de la création de la commande");
    },
  });

  const updateOrderMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrderData }) =>
      orderService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", id] });
      success("Commande modifiée avec succès");
      navigate(`/orders/${id}`);
    },
    onError: (err: any) => {
      const data = err?.response?.data;
      const msg = data?.message ?? data?.error?.message;
      showError(msg || "Erreur lors de la modification de la commande");
    },
  });

  const onSubmit = (data: OrderFormData) => {
    if (!selectedCustomer) {
      showError("Veuillez sélectionner un client");
      return;
    }

    if (allCartItems.length === 0) {
      showError("Veuillez ajouter au moins un produit");
      return;
    }

    if (hasUnavailableProducts) {
      showError(
        "Certains produits du panier ne sont pas disponibles pour la date de livraison choisie. Retirez-les ou changez la date.",
      );
      return;
    }

    if (isBlockedByDeadline) {
      showError("Les commandes pour aujourd'hui sont closes après 10h00");
      return;
    }

    const isDelivery = data.serviceType === "MEAL_DELIVERY";
    if (isDelivery && !customerAddresses.length) {
      showError("Ce client n'a aucune adresse. Ajoutez au moins une adresse au client avant de créer une commande.");
      return;
    }
    if (isDelivery && !selectedAddressDeliverable) {
      showError(OUT_OF_DELIVERY_ZONE_MESSAGE);
      return;
    }

    const defaultAddr = customerAddresses.find((a) => a.isDefault) ?? customerAddresses[0];
    const addressId = isDelivery
      ? (data.customerAddressId || defaultAddr?.id || undefined)
      : (data.customerAddressId || undefined);
    const effectivePaymentStatusId =
      isForcedOnAccount && statusEnAttente?.id
        ? statusEnAttente.id
        : (data.paymentStatusId || undefined);
    const effectivePaymentMethodId =
      isForcedOnAccount ? undefined : (data.paymentMethodId || undefined);

    if (isEditMode && orderId) {
      const orderDeliveryDate = order?.deliveryDate?.slice(0, 10) ?? data.deliveryDate;
      const itemsForOrder = cartByDate[orderDeliveryDate] ?? [];
      const effectiveFee = effectiveDeliveryFee;
      const updateData: UpdateOrderData = {
        customerId: selectedCustomer.id,
        customerAddressId: addressId ?? undefined,
        deliveryDate: data.deliveryDate,
        deliveryTimeSlot: data.deliveryTimeSlot,
        serviceType: data.serviceType,
        recurrence: data.recurrence,
        paymentMethodId: effectivePaymentMethodId,
        paymentStatusId: effectivePaymentStatusId,
        specialInstructions: data.specialInstructions || undefined,
        discountAmount: data.discountAmount != null && Number(data.discountAmount) > 0 ? Number(data.discountAmount) : undefined,
        deliveryCost: effectiveFee,
        items: itemsForOrder.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          isSwitch: item.isSwitch ?? false,
        })),
      };
      updateOrderMutation.mutate({ id: orderId, data: updateData });
      return;
    }

    const baseOrderDataWithoutDiscount: Omit<CreateOrderData, "deliveryDate" | "items" | "discountAmount" | "deliveryCost"> = {
      customerId: selectedCustomer.id,
      customerAddressId: addressId ?? undefined,
      orderDate: getBusinessToday(),
      deliveryTimeSlot: data.deliveryTimeSlot,
      serviceType: data.serviceType,
      recurrence: data.recurrence,
      paymentMethodId: effectivePaymentMethodId,
      paymentStatusId: effectivePaymentStatusId,
      specialInstructions: data.specialInstructions || undefined,
    };

    if (cartDatesSorted.length === 1) {
      const date = cartDatesSorted[0];
      const dayItems = cartByDate[date] ?? [];
      const items = dayItems.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
        isSwitch: item.isSwitch ?? false,
      }));
      const daySub = dayItems.reduce((s, i) => s + i.subtotal, 0);
      const effectiveFee = effectiveDeliveryFee;
      const dayAmount = daySub + effectiveFee;
      const dayDiscount = Math.min(discountByDate[date] ?? 0, dayAmount);
      createOrderMutation.mutate({
        ...baseOrderDataWithoutDiscount,
        deliveryDate: date,
        items,
        discountAmount: dayDiscount > 0 ? dayDiscount : undefined,
        deliveryCost: effectiveFee,
      });
      return;
    }

    // Plusieurs jours : créer une commande par jour
    setIsCreatingMultiple(true);
    (async () => {
      try {
        for (const date of cartDatesSorted) {
          const dayItems = cartByDate[date] ?? [];
          const items = dayItems.map((item) => ({
            productId: item.productId,
            quantity: item.quantity,
            isSwitch: item.isSwitch ?? false,
          }));
          const daySub = dayItems.reduce((s, i) => s + i.subtotal, 0);
          const effectiveFee = computeDeliveryFee(
            data.serviceType,
            zoneDeliveryCost,
            fullCustomer ?? selectedCustomer ?? null,
          );
          const dayAmount = daySub + effectiveFee;
          const dayDiscount = Math.min(discountByDate[date] ?? 0, dayAmount);
          await orderService.create({
            ...baseOrderDataWithoutDiscount,
            deliveryDate: date,
            items,
            discountAmount: dayDiscount > 0 ? dayDiscount : undefined,
            deliveryCost: effectiveFee,
          });
        }
        queryClient.invalidateQueries({ queryKey: ["orders"] });
        success(
          cartDatesSorted.length === 1
            ? "Commande créée avec succès"
            : `${cartDatesSorted.length} commandes créées avec succès`,
        );
        navigate("/orders", {
          state: { deliveryDate: cartDatesSorted[0] },
        });
      } catch (err) {
        showError(
          err instanceof Error ? err.message : "Erreur lors de la création des commandes",
        );
      } finally {
        setIsCreatingMultiple(false);
      }
    })();
  };

  useEffect(() => {
    if (!selectedCustomer?.id || !customerAddresses.length) return;
    const defaultAddr = customerAddresses.find((a) => a.isDefault) ?? customerAddresses[0];
    if (defaultAddr && !customerAddressId) {
      setValue("customerAddressId", defaultAddr.id);
    }
  }, [selectedCustomer?.id, customerAddresses, setValue, customerAddressId]);

  useEffect(() => {
    if (serviceType !== "MEAL_DELIVERY") return;
    if (!selectedCustomerAddress) return;
    if (!selectedAddressDeliverable) {
      setValue("serviceType", "CATERING");
    }
  }, [serviceType, selectedCustomerAddress, selectedAddressDeliverable, setValue]);

  // Préremplir la réduction par jour
  const prevCustomerIdRef = useRef<string | null>(null);
  // Clés stables pour éviter une boucle infinie (objets amountByDate/fullCustomer changent de référence chaque rendu)
  const amountByDateKey = useMemo(
    () => cartDatesSorted.map((d) => `${d}:${amountByDate[d] ?? 0}`).join(";"),
    [cartDatesSorted, amountByDate],
  );
  const fullCustomerRulesKey = `${fullCustomer?.id ?? ""}-${fullCustomer?.enterprise?.discountRules?.length ?? 0}`;
  useEffect(() => {
    if (isEditMode) return;
    if (!selectedCustomer) return;
    const useEnterpriseRule = !!(
      selectedCustomer.enterpriseId &&
      selectedCustomer.indice != null
    );
    // Quand le client a une entreprise + indice, utiliser fullCustomer (avec discountRules) pour calculer la réduction par jour
    if (useEnterpriseRule && fullCustomer?.enterprise?.discountRules?.length) {
      setDiscountByDate((prev) => {
        const next = { ...prev };
        cartDatesSorted.forEach((date) => {
          const dayAmount = amountByDate[date] ?? 0;
          next[date] = getDiscountFromEnterpriseRule(fullCustomer, dayAmount);
        });
        return next;
      });
      return;
    }
    const defaultDiscount =
      selectedCustomer.discountAmount != null
        ? Number(selectedCustomer.discountAmount)
        : 0;
    const customerId = selectedCustomer.id;
    const customerChanged = prevCustomerIdRef.current !== customerId;
    prevCustomerIdRef.current = customerId;
    setDiscountByDate((prev) => {
      const next = { ...prev };
      cartDatesSorted.forEach((date) => {
        if (customerChanged || next[date] === undefined) next[date] = defaultDiscount;
      });
      return next;
    });
  }, [isEditMode, selectedCustomer, cartDatesSorted, fullCustomerRulesKey, amountByDateKey]);

  const useEnterpriseRuleWithRules =
    !isEditMode &&
    !!selectedCustomer?.enterpriseId &&
    selectedCustomer.indice != null &&
    !!fullCustomer?.enterprise?.discountRules?.length;

  if (customersLoading || productsLoading) {
    return <LoadingSpinner message="Chargement des données..." />;
  }

  if (isEditMode && orderLoading) {
    return <LoadingSpinner message="Chargement de la commande..." />;
  }
  if (isEditMode && orderId && orderError) {
    return (
      <ErrorMessage
        message="Commande introuvable ou erreur lors du chargement."
        onRetry={() => queryClient.invalidateQueries({ queryKey: ["orders", orderId] })}
        retryLabel="Réessayer"
      />
    );
  }
  if (isEditMode && orderId && !order) {
    return <LoadingSpinner message="Chargement de la commande..." />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pb: 4 }}>
      <form onSubmit={handleSubmit((data: OrderFormData) => onSubmit(data))} autoComplete="off">
        <Grid container spacing={3}>
          {/* COLONNE 1 : CLIENT (3 colonnes sur 12) */}
          <Grid item xs={12} lg={3}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <PersonSearch sx={{ color: "primary.main" }} />
                <Typography
                  variant="h3"
                  sx={{ fontSize: "18px", fontWeight: 700 }}
                >
                  Client
                </Typography>
              </Box>

              {/* Recherche Client */}
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => formatCustomerName(option)}
                inputValue={customerSearchInput}
                onInputChange={(_, value) => setCustomerSearchInput(value)}
                filterOptions={(options, state) => {
                  const search = state.inputValue.toLowerCase().trim();
                  if (!search) return options;
                  const searchNoSpaces = search.replace(/\s/g, "");
                  return options.filter((opt) => {
                    const fullName = formatCustomerName(opt).toLowerCase();
                    const firstName = opt.firstName?.toLowerCase() ?? "";
                    const lastName = opt.lastName?.toLowerCase() ?? "";
                    const contact = opt.contactPerson?.toLowerCase() ?? "";
                    const email = opt.email?.toLowerCase() ?? "";
                    const phone = (opt.phone ?? "").replace(/\s/g, "");
                    const company = opt.enterprise?.name?.toLowerCase() ?? "";
                    return (
                      fullName.includes(search) ||
                      firstName.includes(search) ||
                      lastName.includes(search) ||
                      contact.includes(search) ||
                      email.includes(search) ||
                      phone.includes(searchNoSpaces) ||
                      company.includes(search)
                    );
                  });
                }}
                value={selectedCustomer}
                onChange={(_, newValue) => {
                  setSelectedCustomer(newValue);
                  setValue("customerId", newValue?.id || "");
                  setValue("customerAddressId", "");
                  setValue("discountAmount", newValue?.discountAmount != null ? Number(newValue.discountAmount) : undefined);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    type="text"
                    placeholder="Rechercher par nom, prénom, téléphone ou entreprise..."
                    size="small"
                    inputProps={{
                      ...params.inputProps,
                      type: "text",
                      autoComplete: "off",
                      "aria-autocomplete": "list",
                      name: "customer-search-field",
                      "data-lpignore": "true",
                      "data-1p-ignore": "true",
                      "data-form-type": "other",
                      readOnly: true,
                      onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        e.target.removeAttribute("readonly");
                        params.inputProps?.onFocus?.(e as React.FocusEvent<HTMLInputElement>);
                      },
                      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        e.target.setAttribute("readonly", "readonly");
                        params.inputProps?.onBlur?.(e as React.FocusEvent<HTMLInputElement>);
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Box component="span" sx={{ mr: 1 }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {(() => {
                const showCreateClient =
                  !isEditMode &&
                  !selectedCustomer &&
                  customerSearchInput.trim().length >= 2 &&
                  customersMatchingSearch.length === 0;
                if (showCreateClient) {
                  createCustomerSuggestedNameRef.current = customerSearchInput.trim();
                }
                return showCreateClient ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Alert severity="info" sx={{ mt: 5, py: 0.5 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Aucun client trouvé pour {customerSearchInput.trim()}
                      </Typography>
                      <Button
                        type="button"
                        variant="outline"
                        size="small"
                        startIcon={<Person />}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const suggestedName = createCustomerSuggestedNameRef.current;
                          navigate("/customers/new", {
                            state: { fromOrder: true, suggestedName },
                          });
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        Créer un client
                      </Button>
                    </Alert>
                  </Box>
                ) : null;
              })()}

              {/* Carte Client Sélectionné */}
              {selectedCustomer && (
                <Card
                  sx={{
                    border: 2,
                    borderColor: "primary.main",
                    borderOpacity: 0.3,
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 96,
                      height: 96,
                      bgcolor: "primary.main",
                      opacity: 0.05,
                      borderRadius: "0 0 0 96px",
                      transform: "translate(25%, -25%)",
                    },
                  }}
                >
                  <Box sx={{ position: "relative", zIndex: 1, p: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: "primary.main",
                            fontSize: "18px",
                            fontWeight: 700,
                          }}
                        >
                          {(selectedCustomer.firstName?.charAt(0) || selectedCustomer.lastName?.charAt(0) || "?").toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            {formatCustomerName(selectedCustomer)}
                          </Typography>
                          <CustomerTypeBadge
                            type={selectedCustomer.type}
                            size="small"
                          />
                          <Link
                            component={RouterLink}
                            to={`/customers/${selectedCustomer.id}`}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.5,
                              fontSize: "0.8125rem",
                            }}
                          >
                            <OpenInNew sx={{ fontSize: 14 }} />
                            Voir la fiche client
                          </Link>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedCustomer(null);
                          setValue("customerId", "");
                          setValue("customerAddressId", "");
                          setValue("discountAmount", undefined);
                        }}
                        sx={{ color: "text.secondary" }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      {customerAddresses.length > 0 && (
                        <Controller
                          name="customerAddressId"
                          control={control}
                          render={({ field }) => (
                            <FormControl fullWidth size="small" required={serviceType === "MEAL_DELIVERY"}>
                              <InputLabel>Adresse de livraison</InputLabel>
                              <Select
                                {...field}
                                value={field.value || ""}
                                label="Adresse de livraison"
                                onChange={(e) => field.onChange(e.target.value)}
                              >
                                {customerAddresses.map((addr) => (
                                  <MenuItem key={addr.id} value={addr.id}>
                                    {addr.label && `${addr.label} — `}
                                    {addr.address}
                                    {addr.city && `, ${addr.postalCode || ""} ${addr.city}`}
                                    {addr.isDefault && " (défaut)"}
                                    {!isAddressDeliverable(addr) ? " (hors zone)" : ""}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                      )}
                      {customerAddresses.length > 0 && (() => {
                        const addr = selectedCustomerAddress;
                        return addr ? (
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                            <LocationOn sx={{ fontSize: 20, color: "text.secondary", mt: 0.5 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {addr.address}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {addr.postalCode} {addr.city}
                              </Typography>
                              {isAddressDeliverable(addr) && addr.zone ? (
                                <Chip
                                  label={addr.zone.name}
                                  size="small"
                                  sx={{ mt: 0.5, fontSize: "10px", height: 20, bgcolor: "error.main", color: "white", fontWeight: 700 }}
                                />
                              ) : (
                                <Chip
                                  label="Hors zone de livraison"
                                  size="small"
                                  color="warning"
                                  sx={{ mt: 0.5, fontSize: "10px", height: 20, fontWeight: 700 }}
                                />
                              )}
                            </Box>
                          </Box>
                        ) : null;
                      })()}
                      {selectedCustomerAddress && !selectedAddressDeliverable ? (
                        <Alert severity="warning" sx={{ py: 0.5 }}>
                          {OUT_OF_DELIVERY_ZONE_MESSAGE}
                        </Alert>
                      ) : null}
                      {!hasDeliverableAddress && customerAddresses.length > 0 ? (
                        <Alert severity="info" sx={{ py: 0.5 }}>
                          Aucune adresse de ce client n&apos;est livrable. Utilisez le mode « À emporter
                          ».
                        </Alert>
                      ) : null}

                      {selectedCustomer.phone && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Phone
                            sx={{ fontSize: 20, color: "text.secondary" }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {selectedCustomer.phone}
                          </Typography>
                        </Box>
                      )}

                      {selectedCustomer.email && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Email
                            sx={{ fontSize: 20, color: "text.secondary" }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {selectedCustomer.email}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Préférences, allergènes, notes (fiche client) */}
                    {fullCustomer && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {/* Préférences de commande */}
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: "primary.main", textTransform: "uppercase", display: "block", mb: 0.5 }}>
                              Préférences de commande
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {fullCustomer.takesStarter !== false && (
                                <Chip size="small" label="Entrée" variant="outlined" sx={{ height: 20, fontSize: "10px" }} />
                              )}
                              {fullCustomer.takesMain !== false && (
                                <Chip size="small" label="Plat" variant="outlined" sx={{ height: 20, fontSize: "10px" }} />
                              )}
                              {fullCustomer.takesDessert !== false && (
                                <Chip size="small" label="Dessert" variant="outlined" sx={{ height: 20, fontSize: "10px" }} />
                              )}
                              {(fullCustomer.takesStarter === false && fullCustomer.takesMain === false && fullCustomer.takesDessert === false) && (
                                <Typography variant="caption" color="text.secondary">Non renseigné</Typography>
                              )}
                              {fullCustomer.proteinPreference && (
                                <Chip
                                  size="small"
                                  label={
                                    fullCustomer.proteinPreference === "FISH"
                                      ? "Poisson"
                                      : fullCustomer.proteinPreference === "MEAT"
                                        ? "Viande"
                                        : fullCustomer.proteinPreference === "BOTH"
                                          ? "Les deux"
                                          : fullCustomer.proteinPreference === "VEGETARIAN"
                                            ? "Végé"
                                            : fullCustomer.proteinPreference
                                  }
                                  sx={{ height: 20, fontSize: "10px" }}
                                />
                              )}
                              {fullCustomer.prefersPremium === true && (
                                <Chip size="small" label="Premium" color="primary" variant="outlined" sx={{ height: 20, fontSize: "10px" }} />
                              )}
                              {fullCustomer.prefersVegetarian === true && (
                                <Chip size="small" label="Végétarien" color="success" variant="outlined" sx={{ height: 20, fontSize: "10px" }} />
                              )}
                            </Box>
                          </Box>

                          {/* Allergènes / intolérances */}
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: "error.main", textTransform: "uppercase", display: "block", mb: 0.5 }}>
                              Allergies / intolérances
                            </Typography>
                            {fullCustomer.allergens && fullCustomer.allergens.length > 0 ? (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {fullCustomer.allergens.map((ca) => (
                                  <AllergenChip
                                    key={ca.allergen.id}
                                    label={ca.allergen.icon ? `${ca.allergen.icon} ${ca.allergen.name}` : ca.allergen.name}
                                    compact
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">Aucune</Typography>
                            )}
                          </Box>

                          {/* Notes internes */}
                          {fullCustomer.notes && (
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", display: "block", mb: 0.5 }}>
                                Notes internes
                              </Typography>
                              <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary", whiteSpace: "pre-wrap" }}>
                                {fullCustomer.notes}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                </Card>
              )}
            </Box>
          </Grid>

          {/* COLONNE 2 : MENU (5 colonnes sur 12) */}
          <Grid item xs={12} lg={5}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Date de livraison : au-dessus du menu pour filtrer les plats éligibles */}
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}
              >
                Date de livraison souhaitée
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (!deliveryDate) return;
                    const todayStr = getBusinessToday();
                    const prev = format(subDays(parseLocalDateOnly(deliveryDate), 1), "yyyy-MM-dd");
                    if (prev >= todayStr) setValue("deliveryDate", prev);
                  }}
                  disabled={
                    !deliveryDate ||
                    deliveryDate <= getBusinessToday()
                  }
                  aria-label="Jour précédent"
                  sx={{ border: 1, borderColor: "divider" }}
                >
                  <ChevronLeft />
                </IconButton>
                <Controller
                  name="deliveryDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Date"
                      type="date"
                      size="small"
                      fullWidth
                      error={!!errors.deliveryDate}
                      helperText={errors.deliveryDate?.message}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: getBusinessToday(),
                      }}
                      sx={{ minWidth: 150 }}
                    />
                  )}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    if (!deliveryDate) return;
                    const next = format(addDays(parseLocalDateOnly(deliveryDate), 1), "yyyy-MM-dd");
                    setValue("deliveryDate", next);
                  }}
                  disabled={!deliveryDate}
                  aria-label="Jour suivant"
                  sx={{ border: 1, borderColor: "divider" }}
                >
                  <ChevronRight />
                </IconButton>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() =>
                    setValue("deliveryDate", getBusinessToday())
                  }
                  sx={{ whiteSpace: "nowrap", ml: 0.5 }}
                >
                  Aujourd'hui
                </Button>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Restaurant sx={{ color: "primary.main" }} />
                  <Typography
                    variant="h3"
                    sx={{ fontSize: "18px", fontWeight: 700 }}
                  >
                    Menu du jour
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {deliveryDate
                    ? format(parseLocalDateOnly(deliveryDate), "d MMM yyyy", { locale: fr })
                    : format(getBusinessNow(), "d MMM yyyy", { locale: fr })}
                </Typography>
              </Box>

              {/* Filtres Catégories */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  overflowX: "auto",
                  pb: 1,
                  scrollbarWidth: "none",
                }}
              >
                <Button
                  type="button"
                  variant={categoryFilter === null ? "primary" : "outline"}
                  size="small"
                  onClick={() => setCategoryFilter(null)}
                  sx={{ borderRadius: "9999px", whiteSpace: "nowrap", minWidth: "auto", px: 2 }}
                >
                  Tous
                </Button>
                {categories.map((cat) => (
                  <Button
                    key={cat.id}
                    type="button"
                    variant={categoryFilter === cat.id ? "primary" : "outline"}
                    size="small"
                    onClick={() =>
                      setCategoryFilter(
                        categoryFilter === cat.id ? null : cat.id,
                      )
                    }
                    sx={{ borderRadius: "9999px", whiteSpace: "nowrap", minWidth: "auto", px: 2 }}
                  >
                    {cat.name}
                  </Button>
                ))}
              </Box>

              {/* Recherche Produits */}
              <TextField
                placeholder="Rechercher un produit..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box component="span" sx={{ mr: 1 }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Liste des Produits (éligibles pour la date sélectionnée) */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  maxHeight: 600,
                  overflowY: "auto",
                  pr: 1,
                }}
              >
                {filteredProducts.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: "center",
                      color: "text.secondary",
                    }}
                  >
                    <Restaurant sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                    <Typography variant="body2">
                      {!deliveryDate
                        ? "Sélectionnez une date de livraison pour afficher les plats éligibles."
                        : eligibleProductsForDate.length === 0
                          ? "Aucun plat au menu pour cette date. Vérifiez le menu hebdomadaire."
                          : productSearch || categoryFilter
                            ? "Aucun produit ne correspond à votre recherche."
                            : "Aucun produit disponible."}
                    </Typography>
                  </Box>
                ) : (
                filteredProducts.map((product) => {
                  const cartItem = cartItemsForCurrentDate.find(
                    (item) => item.productId === product.id,
                  );
                  const quantity = cartItem?.quantity || 0;

                  return (
                    <Box
                      key={product.id}
                      sx={{
                        p: 1.5,
                        bgcolor: "background.paper",
                        borderRadius: "12px",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        border: "1px solid",
                        borderColor: "transparent",
                        display: "flex",
                        gap: 2,
                        alignItems: "flex-start",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "rgba(231, 126, 35, 0.3)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          flexShrink: 0,
                          borderRadius: "8px",
                          bgcolor: "grey.100",
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          ...(product.imageUrl && {
                            backgroundImage: `url(${product.imageUrl})`,
                          }),
                        }}
                      >
                        {!product.imageUrl && (
                          <Restaurant sx={{ color: "grey.400", fontSize: 32 }} />
                        )}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "flex-start",
                              gap: 0.75,
                              minWidth: 0,
                              flex: 1,
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 700,
                                color: "text.primary",
                                fontSize: "14px",
                                lineHeight: 1.35,
                                wordBreak: "break-word",
                              }}
                            >
                              {product.name}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              color: "secondary.main",
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                          >
                            {Number(product.price).toFixed(2)}€
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "text.secondary",
                            mt: 0.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            lineHeight: 1.4,
                          }}
                        >
                          {product.description || "Aucune description"}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                          {product.category && (
                            <Box
                              component="span"
                              sx={{
                                fontSize: "10px",
                                px: 0.75,
                                py: 0.25,
                                bgcolor: "#dbeafe",
                                color: "#1d4ed8",
                                borderRadius: "4px",
                                border: "1px solid #bfdbfe",
                              }}
                            >
                              {product.category.name}
                            </Box>
                          )}
                          {product.isVegetarian && (
                            <Box
                              component="span"
                              sx={{
                                fontSize: "10px",
                                px: 0.75,
                                py: 0.25,
                                bgcolor: "#dcfce7",
                                color: "#15803d",
                                borderRadius: "4px",
                                border: "1px solid #bbf7d0",
                              }}
                            >
                              Végétarien
                            </Box>
                          )}
                          {product.isRecurrent && (
                            <Box
                              component="span"
                              sx={{
                                fontSize: "10px",
                                px: 0.75,
                                py: 0.25,
                                bgcolor: "#f3e8ff",
                                color: "#7c3aed",
                                borderRadius: "4px",
                                border: "1px solid #e9d5ff",
                              }}
                            >
                              Récurrent
                            </Box>
                          )}
                          {platsDuJourSwitchProductIds.has(product.id) && (
                            <Box
                              component="span"
                              sx={{
                                fontSize: "10px",
                                px: 0.75,
                                py: 0.25,
                                bgcolor: "#fef3c7",
                                color: "#b45309",
                                borderRadius: "4px",
                                border: "1px solid #fde68a",
                              }}
                            >
                              Switch
                            </Box>
                          )}
                          {product.isPremium && (
                            <Box
                              component="span"
                              sx={{
                                fontSize: "10px",
                                px: 0.75,
                                py: 0.25,
                                bgcolor: "#fef9c3",
                                color: "#a16207",
                                borderRadius: "4px",
                                border: "1px solid #fef08a",
                              }}
                            >
                              Premium
                            </Box>
                          )}
                          {product.isSpicy && (
                            <Box
                              component="span"
                              sx={{
                                fontSize: "10px",
                                px: 0.75,
                                py: 0.25,
                                bgcolor: "#fee2e2",
                                color: "#b91c1c",
                                borderRadius: "4px",
                                border: "1px solid #fecaca",
                              }}
                            >
                              Épicé
                            </Box>
                          )}
                          {exhaustedProductIds.has(product.id) && (
                            <Box
                              component="span"
                              sx={{
                                fontSize: "10px",
                                px: 0.75,
                                py: 0.25,
                                bgcolor: "#f3f4f6",
                                color: "#6b7280",
                                borderRadius: "4px",
                                border: "1px solid #e5e7eb",
                              }}
                            >
                              Épuisé
                            </Box>
                          )}
                        </Box>
                        {displayedProductAllergensMap[product.id]?.length > 0 && (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                            {displayedProductAllergensMap[product.id].map((a) => (
                              <AllergenChip
                                key={a.id}
                                label={a.icon ? `${a.icon} ${a.code}` : a.code}
                                compact
                              />
                            ))}
                          </Box>
                        )}
                        {platsDuJourSwitchProductIds.has(product.id) && quantity > 0 && (
                          <FormControlLabel
                            sx={{ mt: 0.5, ml: 0 }}
                            control={
                              <Switch
                                size="small"
                                color="success"
                                checked={cartItem?.isSwitch ?? false}
                                onChange={() =>
                                  handleToggleSwitch(product.id, deliveryDateStr)
                                }
                              />
                            }
                            label={
                              <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                                Formule Switch (3/4 légumes, 1/4 féculents)
                              </Typography>
                            }
                          />
                        )}
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.5,
                          bgcolor: "grey.50",
                          borderRadius: "8px",
                          p: 0.5,
                        }}
                      >
                        <Box
                          component="button"
                          type="button"
                          disabled={exhaustedProductIds.has(product.id)}
                          onClick={() => handleAddProduct(product)}
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "4px",
                            bgcolor: exhaustedProductIds.has(product.id) ? "grey.200" : "white",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: exhaustedProductIds.has(product.id) ? "grey.500" : "secondary.main",
                            border: "none",
                            cursor: exhaustedProductIds.has(product.id) ? "not-allowed" : "pointer",
                            transition: "all 0.15s",
                            "&:hover": exhaustedProductIds.has(product.id) ? {} : { bgcolor: "grey.50" },
                            "&:active": exhaustedProductIds.has(product.id) ? {} : { transform: "scale(0.95)" },
                          }}
                        >
                          <Add sx={{ fontSize: 16 }} />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 700,
                            width: 24,
                            textAlign: "center",
                            color: quantity > 0 ? "text.primary" : "grey.300",
                          }}
                        >
                          {quantity}
                        </Typography>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => quantity > 0 && handleUpdateQuantity(product.id, deliveryDateStr, -1)}
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "4px",
                            bgcolor: "white",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: quantity > 0 ? "grey.400" : "grey.300",
                            border: "none",
                            cursor: quantity > 0 ? "pointer" : "default",
                            transition: "all 0.15s",
                            pointerEvents: quantity === 0 ? "none" : "auto",
                            "&:hover": quantity > 0 ? { bgcolor: "grey.50", color: "secondary.main" } : {},
                            "&:active": quantity > 0 ? { transform: "scale(0.95)" } : {},
                          }}
                        >
                          <Remove sx={{ fontSize: 16 }} />
                        </Box>
                      </Box>
                    </Box>
                  );
                }))}
              </Box>
            </Box>
          </Grid>

          {/* COLONNE 3 : SUMMARY (4 colonnes sur 12) */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <ReceiptLong sx={{ color: "primary.main" }} />
                <Typography
                  variant="h3"
                  sx={{ fontSize: "18px", fontWeight: 700 }}
                >
                  Récapitulatif
                </Typography>
              </Box>

              <Card
                sx={{
                  borderTop: 4,
                  borderColor: "primary.main",
                  position: "sticky",
                  top: 16,
                }}
              >

                {/* Deadline Warning */}
                {isDeadlinePassed && deliveryDate === getBusinessToday() && (
                  <Box sx={{ m: 2 }}>
                    <CompactAlert
                      severity={canBypassDeadline ? "warning" : "error"}
                      icon={<Schedule fontSize="small" />}
                      message={
                        canBypassDeadline
                          ? "Heure limite dépassée"
                          : "Commandes du jour closes"
                      }
                      detail={
                        canBypassDeadline ? (
                          <>
                            Il est plus de 10h00. Vous pouvez tout de même valider cette
                            commande pour aujourd&apos;hui (dépassement de l&apos;heure limite).
                          </>
                        ) : (
                          <>
                            Il est plus de 10h00. Les commandes pour aujourd&apos;hui sont
                            closes. Les nouvelles commandes seront planifiées pour demain.
                          </>
                        )
                      }
                    />
                  </Box>
                )}

                {/* Alerte : client a déjà une commande sur un des jours sélectionnés */}
                {!isEditMode && existingOrdersOnSelectedDates.length > 0 && (
                  <Box sx={{ m: 2 }}>
                    <CompactAlert
                      severity="warning"
                      icon={<Person fontSize="small" />}
                      message="Commande(s) déjà enregistrée(s) pour ce client"
                      detail={
                        <>
                          Ce client a déjà une commande enregistrée pour le(s) jour(s) suivant(s) :{" "}
                          <strong>{existingOrderDatesFormatted}</strong>. Vérifiez si vous souhaitez
                          ajouter une seconde commande ou modifier l&apos;existante.
                        </>
                      }
                    />
                  </Box>
                )}

                {/* Alerte : produits du panier non disponibles pour la date de livraison */}
                {hasUnavailableProducts && (
                  <Box sx={{ m: 2 }}>
                    <CompactAlert
                      severity="error"
                      icon={<WarningAmber fontSize="small" />}
                      message="Produits indisponibles pour cette date"
                      detail={
                        <>
                          Vous avez changé le jour de livraison. Les produits suivants ne sont pas
                          au menu pour ce jour :
                          <Box component="ul" sx={{ mt: 1, mb: 1, pl: 2 }}>
                            {unavailableCartItems.map((item) => (
                              <li key={item.productId}>
                                <strong>{item.product.name}</strong> ({item.quantity}x)
                              </li>
                            ))}
                          </Box>
                          Retirez-les du panier ou choisissez une autre date de livraison pour
                          pouvoir créer la commande.
                        </>
                      }
                    />
                  </Box>
                )}

                {/* Alerte allergènes : produits du panier contenant un allergène du client */}
                {allergenConflictDetails.length > 0 && (
                  <Box sx={{ m: 2 }}>
                    <CompactAlert
                      severity="warning"
                      icon={<WarningAmber fontSize="small" />}
                      message="Allergènes / intolérances client"
                      detail={
                        <>
                          Le client a des allergènes renseignés sur sa fiche. Les produits suivants
                          en contiennent :
                          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                            {allergenConflictDetails.map((c, i) => (
                              <li key={i}>
                                <strong>{c.productName}</strong> —{" "}
                                {c.matchingAllergens
                                  .map((a) => (a.icon ? `${a.icon} ${a.name}` : a.name))
                                  .join(", ")}
                              </li>
                            ))}
                          </Box>
                        </>
                      }
                    />
                  </Box>
                )}

                {!isEditMode && serviceType === "MEAL_DELIVERY" && (
                  <Box sx={{ m: 2 }}>
                    <CompactAlert
                      severity="info"
                      message="Un seul forfait de livraison par commande"
                      detail={
                        <>
                          Une commande avec livraison comporte{" "}
                          <strong>un seul forfait de livraison</strong>, quel que soit le nombre
                          de plats ou d&apos;articles. Une livraison correspond à{" "}
                          <strong>un seul paiement</strong> et à{" "}
                          <strong>un seul interlocuteur</strong> sur place — afin d&apos;éviter les
                          regroupements de commandes pour partager les frais lorsque plusieurs
                          encaissements sont nécessaires ou lorsque le livreur doit attendre sur
                          place.
                        </>
                      }
                    />
                  </Box>
                )}

                {/* Items du panier — groupés par jour */}
                <Box sx={{ p: 2.5, maxHeight: 400, overflowY: "auto" }}>
                  {cartDatesSorted.length === 0 ? (
                    <CompactAlert
                      severity="warning"
                      message="Panier vide"
                      detail="Aucun produit dans le panier. Sélectionnez une date puis ajoutez des produits."
                    />
                  ) : (
                    <>
                      {cartDatesSorted.map((date) => {
                        const dayItems = cartByDate[date] ?? [];
                        const daySubtotal = dayItems.reduce((s, i) => s + i.subtotal, 0);
                        const dayAmount = daySubtotal + effectiveDeliveryFee;
                        const dateLabel = format(parseLocalDateOnly(date), "EEEE d MMMM yyyy", { locale: fr });
                        return (
                          <Box
                            key={date}
                            sx={{
                              mb: 2,
                              "&:not(:first-of-type)": { mt: 2, pt: 2, borderTop: "1px dashed", borderColor: "divider" },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                fontWeight: 700,
                                textTransform: "capitalize",
                                color: "primary.main",
                                mb: 1,
                              }}
                            >
                              {dateLabel}
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              {dayItems.map((item) => (
                                <Box
                                  key={`${date}-${item.productId}`}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" noWrap>
                                      {item.product.name}
                                    </Typography>
                                    {item.isSwitch && (
                                      <Typography
                                        variant="caption"
                                        sx={{ display: "block", color: "success.main", fontWeight: 600 }}
                                      >
                                        Formule Switch
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                                    <IconButton
                                      size="small"
                                      aria-label="Diminuer la quantité"
                                      onClick={() => handleUpdateQuantity(item.productId, date, -1)}
                                      sx={{
                                        p: 0.5,
                                        bgcolor: "action.hover",
                                        "&:hover": { bgcolor: "action.selected" },
                                      }}
                                    >
                                      <Remove fontSize="small" />
                                    </IconButton>
                                    <Typography
                                      variant="body2"
                                      sx={{ minWidth: 24, textAlign: "center", fontWeight: 600 }}
                                    >
                                      {item.quantity}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      aria-label="Augmenter la quantité"
                                      onClick={() => handleUpdateQuantity(item.productId, date, 1)}
                                      sx={{
                                        p: 0.5,
                                        bgcolor: "action.hover",
                                        "&:hover": { bgcolor: "action.selected" },
                                      }}
                                    >
                                      <Add fontSize="small" />
                                    </IconButton>
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontWeight: 500,
                                      minWidth: 48,
                                      textAlign: "right",
                                    }}
                                  >
                                    {item.subtotal.toFixed(2)}€
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                            {/* Réduction pour ce jour (au-dessus du sous-total jour) */}
                            {selectedCustomer && (
                              <Box sx={{ mt: 1 }}>
                                {isEditMode ? (
                                  <Controller
                                    name="discountAmount"
                                    control={control}
                                    render={({ field }) => (
                                      <TextField
                                        {...field}
                                        label="Réduction (€)"
                                        type="text"
                                        inputMode="decimal"
                                        size="small"
                                        fullWidth
                                        value={field.value === undefined || field.value === null ? "" : String(field.value)}
                                        onChange={(e) => {
                                          const v = e.target.value.replace(",", ".");
                                          if (v === "") field.onChange(undefined);
                                          else if (/^\d*\.?\d*$/.test(v)) field.onChange(v);
                                        }}
                                      />
                                    )}
                                  />
                                ) : (
                                  <>
                                    <TextField
                                      label="Réduction (€)"
                                      type="text"
                                      inputMode="decimal"
                                      size="small"
                                      fullWidth
                                      disabled={useEnterpriseRuleWithRules}
                                      helperText={
                                        useEnterpriseRuleWithRules
                                          ? "Calculée selon la règle de l'entreprise"
                                          : undefined
                                      }
                                      value={
                                        discountByDate[date] != null
                                          ? (typeof discountByDate[date] === "number" && Number.isFinite(discountByDate[date])
                                            ? (discountByDate[date] as number).toFixed(2)
                                            : String(discountByDate[date]))
                                          : ""
                                      }
                                      onChange={(e) => {
                                        if (useEnterpriseRuleWithRules) return;
                                        const v = e.target.value.replace(",", ".");
                                        if (v === "") {
                                          setDiscountByDate((prev) => {
                                            const next = { ...prev };
                                            delete next[date];
                                            return next;
                                          });
                                        } else if (/^\d*\.?\d*$/.test(v)) {
                                          setDiscountByDate((prev) => ({ ...prev, [date]: Number(v) || 0 }));
                                        }
                                      }}
                                    />
                                    {(() => {
                                      const effectiveDiscount = Math.min(discountByDate[date] ?? 0, dayAmount);
                                      return effectiveDiscount > 0 ? (
                                        <Typography variant="caption" sx={{ color: "success.main", mt: 0.5, display: "block" }}>
                                          {!useEnterpriseRuleWithRules && effectiveDiscount < (discountByDate[date] ?? 0) && (
                                            <Typography component="span" variant="caption" sx={{ color: "text.secondary", ml: 0.5 }}>
                                              (plafonnée au montant du jour)
                                            </Typography>
                                          )}
                                        </Typography>
                                      ) : null;
                                    })()}
                                  </>
                                )}
                              </Box>
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                mt: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary", fontFamily: "monospace" }}
                              >
                                Sous-total jour (dont livraison) :{" "}
                                {(Math.max(0, dayAmount - Math.min(discountByDate[date] ?? 0, dayAmount))).toFixed(2)}€
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}

                      <Divider sx={{ my: 2, borderStyle: "dashed" }} />

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            Sous-total produits
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {subtotal.toFixed(2)}€
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            Livraison (forfait / commande){" "}
                            {numberOfDeliveryDays > 1
                              ? `(${numberOfDeliveryDays} jours × ${effectiveDeliveryFee.toFixed(2)}€)`
                              : (() => {
                                  const addr = customerAddressId
                                    ? customerAddresses.find((a) => a.id === customerAddressId)
                                    : customerAddresses.find((a) => a.isDefault) ?? customerAddresses[0];
                                  return addr?.zone?.name ? `(${addr.zone.name})` : "";
                                })()}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {deliveryTotal.toFixed(2)}€
                          </Typography>
                        </Box>
                        {totalDiscountNum > 0 && (
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="caption" sx={{ color: "success.main" }}>
                              Réduction
                            </Typography>
                            <Typography variant="caption" sx={{ color: "success.main" }}>
                              -{totalDiscountNum.toFixed(2)}€
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            TVA ({(displayVatRate * 100).toFixed(1)}%)
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {displayVatAmount.toFixed(2)}€
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>

                {/* Total */}
                <Box
                  sx={{
                    bgcolor: "grey.900",
                    color: "white",
                    p: 2.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.8, fontWeight: 500 }}
                  >
                    Total à payer
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontFamily: "monospace", fontWeight: 700 }}
                  >
                    {displayTotal.toFixed(2)}€
                  </Typography>
                </Box>

                {/* Règlement : immédiat ou plus tard */}
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    bgcolor: "grey.100",
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase" }}>
                    Règlement
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25 }}>
                    {formatPaymentSettlementLabel(
                      order?.paymentStatus?.name === "Payé" || watch("paymentStatusId") === statusPaye?.id,
                    )}
                  </Typography>
                </Box>

                {/* Formulaire */}
                <Box
                  sx={{
                    p: 2.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {/* Type de service */}
                  <Controller
                    name="serviceType"
                    control={control}
                    render={({ field }) => (
                      <FormControl>
                        <FormLabel
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            mb: 1,
                          }}
                        >
                          Type de service
                        </FormLabel>
                        {isDriverNewOrder ? (
                          <RadioGroup row value="MEAL_DELIVERY" sx={{ pointerEvents: "none" }}>
                            <FormControlLabel
                              value="MEAL_DELIVERY"
                              checked
                              control={<Radio size="small" checked disabled tabIndex={-1} />}
                              label="A livrer"
                            />
                          </RadioGroup>
                        ) : (
                          <RadioGroup {...field} row>
                            <FormControlLabel
                              value="MEAL_DELIVERY"
                              control={<Radio size="small" />}
                              label="A livrer"
                              disabled={!hasDeliverableAddress || !selectedAddressDeliverable}
                            />
                            <FormControlLabel
                              value="CATERING"
                              control={<Radio size="small" />}
                              label="A emporter"
                            />
                          </RadioGroup>
                        )}
                      </FormControl>
                    )}
                  />

                  {/* Règlement : immédiat ou plus tard */}
                  <Controller
                    name="paymentStatusId"
                    control={control}
                    render={({ field }) => (
                      <FormControl>
                        <FormLabel
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            mb: 1,
                          }}
                        >
                          Règlement
                        </FormLabel>
                        {isForcedOnAccount ? (
                          <RadioGroup row value="ON_DELIVERY" sx={{ pointerEvents: "none" }}>
                            <FormControlLabel
                              value="ON_DELIVERY"
                              checked
                              control={<Radio size="small" checked disabled tabIndex={-1} />}
                              label={PAYMENT_SETTLEMENT_LATER_LABEL}
                            />
                          </RadioGroup>
                        ) : isDriverNewOrder ? (
                          <RadioGroup row value="IMMEDIATE" sx={{ pointerEvents: "none" }}>
                            <FormControlLabel
                              value="IMMEDIATE"
                              checked
                              control={<Radio size="small" checked disabled tabIndex={-1} />}
                              label={PAYMENT_SETTLEMENT_IMMEDIATE_LABEL}
                            />
                          </RadioGroup>
                        ) : (
                          <RadioGroup
                            row
                            value={
                              field.value === statusPaye?.id
                                ? "IMMEDIATE"
                                : "ON_DELIVERY"
                            }
                            onChange={(_, value) => {
                              if (value === "IMMEDIATE" && statusPaye) {
                                field.onChange(statusPaye.id);
                              } else if (statusEnAttente) {
                                field.onChange(statusEnAttente.id);
                              }
                            }}
                          >
                            <FormControlLabel
                              value="IMMEDIATE"
                              control={<Radio size="small" />}
                              label={PAYMENT_SETTLEMENT_IMMEDIATE_LABEL}
                            />
                            <FormControlLabel
                              value="ON_DELIVERY"
                              control={<Radio size="small" />}
                              label={PAYMENT_SETTLEMENT_LATER_LABEL}
                            />
                          </RadioGroup>
                        )}
                      </FormControl>
                    )}
                  />

                  {/* Méthode de paiement (affiché uniquement si règlement immédiat) */}
                  {!isForcedOnAccount && watch("paymentStatusId") === statusPaye?.id && (
                    <Controller
                      name="paymentMethodId"
                      control={control}
                      render={({ field }) => (
                        <FormControl component="fieldset">
                          <FormLabel
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              mb: 1,
                            }}
                          >
                            Mode de paiement
                          </FormLabel>
                          {paymentMethods.length > 0 ? (
                            <RadioGroup
                              value={field.value ?? ""}
                              onChange={(e) => field.onChange(e.target.value)}
                              sx={{ gap: 0.5 }}
                            >
                              {paymentMethods.map((method) => (
                                <FormControlLabel
                                  key={method.id}
                                  value={method.id}
                                  control={<Radio size="small" />}
                                  label={method.name}
                                />
                              ))}
                            </RadioGroup>
                          ) : (
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                              Aucune méthode de paiement disponible
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  )}

                  {/* Notes */}
                  <Controller
                    name="specialInstructions"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Instructions spéciales"
                        placeholder="Code d'accès, allergies..."
                        multiline
                        rows={2}
                        size="small"
                      />
                    )}
                  />

                  {/* Bouton créer / enregistrer */}
                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    size="large"
                    loading={
                      createOrderMutation.isPending ||
                      updateOrderMutation.isPending ||
                      isSubmitting ||
                      isCreatingMultiple
                    }
                    startIcon={
                      !createOrderMutation.isPending &&
                      !updateOrderMutation.isPending &&
                      !isSubmitting &&
                      !isCreatingMultiple ? (
                        <ArrowForward />
                      ) : undefined
                    }
                    disabled={
                      isBlockedByDeadline ||
                      hasUnavailableProducts ||
                      isCreatingMultiple
                    }
                  >
                    {createOrderMutation.isPending ||
                    updateOrderMutation.isPending ||
                    isSubmitting ||
                    isCreatingMultiple ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        {isEditMode ? "Enregistrement..." : "Création..."}
                      </>
                    ) : isEditMode ? (
                      "Enregistrer les modifications"
                    ) : numberOfDeliveryDays > 1 ? (
                      `Créer les ${numberOfDeliveryDays} commandes`
                    ) : (
                      "Créer la commande"
                    )}
                  </Button>
                </Box>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}
